import { useCallback, useMemo } from "react";
import _defaults from "lodash/defaults";
import { Button, Form, Input, NumberInput, PickerInput } from "../lib";
import {
  BodyType,
  BustSize,
  CupSize,
  EndowmentType,
  HipSize,
  OrientationType,
  User,
  UserBodyUpdateReq,
  useServerForm,
  useUserBodyUpdate,
  WaistSize,
} from "../services";

const defaultInitialValues: UserBodyUpdateReq = {
  body_type: "slim",
  height: 164,
  bust: 30,
  cup: "C",
  waist: 22,
  hip: 32,
  endowment: "small",
  orientation: "top",
};

type BodyTypeOption = {
  label: string;
  value: BodyType;
};
const BODY_TYPES: BodyTypeOption[] = [
  { label: "Slim", value: "slim" },
  { label: "Average", value: "avg" },
  { label: "Athletic", value: "athletic" },
  { label: "Curvy", value: "curvy" },
];

type BustSizeOption = {
  label: string;
  value: BustSize;
};
const BUST_TYPES: BustSizeOption[] = [
  { label: "30", value: 30 },
  { label: "32", value: 32 },
  { label: "34", value: 34 },
  { label: "36", value: 36 },
  { label: "38", value: 38 },
  { label: "40", value: 40 },
  { label: "42", value: 42 },
  { label: "44", value: 44 },
];

type CupSizeOption = {
  label: string;
  value: CupSize;
};
const CUP_SIZES: CupSizeOption[] = [
  { label: "A", value: "A" },
  { label: "B", value: "B" },
  { label: "C", value: "C" },
  { label: "D", value: "D" },
  { label: "D+", value: "D+" },
  { label: "E", value: "E" },
];

type WaistSizeOption = {
  label: string;
  value: WaistSize;
};
const WAIST_SIZES: WaistSizeOption[] = [
  { label: "20", value: 20 },
  { label: "22", value: 22 },
  { label: "24", value: 24 },
  { label: "26", value: 26 },
  { label: "28", value: 28 },
  { label: "30", value: 30 },
  { label: "32", value: 32 },
  { label: "34", value: 34 },
];

type HipSizeOption = {
  label: string;
  value: HipSize;
};
const HIP_SIZES: HipSizeOption[] = [
  { label: "30", value: 30 },
  { label: "32", value: 32 },
  { label: "34", value: 34 },
  { label: "36", value: 36 },
  { label: "38", value: 38 },
  { label: "40", value: 40 },
  { label: "42", value: 42 },
  { label: "44", value: 44 },
];

type EndowmentOption = {
  label: string;
  value: EndowmentType;
};
const ENDOWMENT_OPTIONS: EndowmentOption[] = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "xlarge", label: "X-Large" },
  { value: "post-op", label: "Post op" },
];

type OrientationOption = {
  label: string;
  value: OrientationType;
};
const ORIENTATION_OPTIONS: OrientationOption[] = [
  { label: "Top", value: "top" },
  { label: "Bottom", value: "bottom" },
  { label: "Versatile", value: "versatile" },
];

type Props = {
  user: User;
  onSuccess?: () => void;
};

export function BodyForm({ user, onSuccess }: Props) {
  // Vars
  const initialValues: UserBodyUpdateReq = useMemo(() => {
    return _defaults(
      {
        body_type: user.body_type ?? undefined,
        height: user.height ?? undefined,
        bust: user.bust ?? undefined,
        cup: user.cup ?? undefined,
        waist: user.waist ?? undefined,
        hip: user.hip ?? undefined,
        endowment: user.endowment ?? undefined,
        orientation: user.orientation ?? undefined,
      },
      defaultInitialValues
    );
  }, [user]);

  const [updateUserBody, isUpdating, error] = useUserBodyUpdate();
  const { errors, values, setValue } = useServerForm(initialValues, error);

  // Handlers
  const handleSubmit = useCallback(async () => {
    await updateUserBody(values);

    onSuccess?.();
  }, [onSuccess, updateUserBody, values]);

  // Render
  const isFemale = user.gender_id === 1;

  return (
    <Form>
      <Input label="Height (cm)" error={errors.height}>
        <NumberInput
          name="height"
          value={values.height}
          onChange={setValue}
          placeholder="e.g. 160"
        />
      </Input>
      <Input label="Body type" error={errors.body_type}>
        <PickerInput
          name="body_type"
          value={values.body_type}
          onChange={setValue}
          options={BODY_TYPES}
        />
      </Input>
      <Input label="Bust size" error={errors.bust}>
        <PickerInput
          name="bust"
          value={values.bust}
          onChange={setValue}
          options={BUST_TYPES}
        />
      </Input>
      <Input label="Cup size" error={errors.cup}>
        <PickerInput
          name="cup"
          value={values.cup}
          onChange={setValue}
          options={CUP_SIZES}
        />
      </Input>
      <Input label="Waist size" error={errors.waist}>
        <PickerInput
          name="waist"
          value={values.waist}
          onChange={setValue}
          options={WAIST_SIZES}
        />
      </Input>
      <Input label="Hip size" error={errors.hip}>
        <PickerInput
          name="hip"
          value={values.hip}
          onChange={setValue}
          options={HIP_SIZES}
        />
      </Input>
      {!isFemale && (
        <>
          <Input label="Endowment" error={errors.endowment}>
            <PickerInput
              name="endowment"
              value={values.endowment}
              onChange={setValue}
              options={ENDOWMENT_OPTIONS}
            />
          </Input>
          <Input label="Orientation" error={errors.orientation}>
            <PickerInput
              name="orientation"
              value={values.orientation}
              onChange={setValue}
              options={ORIENTATION_OPTIONS}
            />
          </Input>
        </>
      )}
      <Button em onPress={handleSubmit} loading={isUpdating}>
        Save changes
      </Button>
    </Form>
  );
}
